import * as React from "react";
import { useTranslation } from 'react-i18next';
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import DialogTitle from "@mui/material/DialogTitle";
import { DialogContent } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { JsonForms } from "@jsonforms/react";
import {
  Delete as DeleteIcon,
  Security as SecurityIcon,
  PlayArrow as PlayArrowIcon,
  SettingsSuggest as SettingsSuggestIcon,
  FormatListBulleted as FormatListBulletedIcon,
  SaveAlt as SaveAltIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Stack from "@mui/material/Stack";
import {
  materialRenderers,
  materialCells,
} from "@jsonforms/material-renderers";
import AppContext from "../../utils/context";
import { schema, uischema } from "./schema";
import { useDialogs } from "@toolpad/core/useDialogs";

export default function ComponentsDrawer(props) {
  const dialogs = useDialogs();
  const appContext = React.useContext(AppContext);
  const { t } = useTranslation(); // Utiliser la fonction de traduction
  const { events, database } = appContext;

  const [isDrawer, setDrawer] = React.useState(false);
  const [secrets, setSecrets] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({});
  const [formErrors, setFormErrors] = React.useState(null);
  const [hasTriedToSave, setHasTriedToSave] = React.useState(false);
  const [instance, setInstance] = React.useState(null);

  const helpSecretsMsg = t('secrets.help.message', { defaultValue: undefined });
  const helpSecretsLink = t('secrets.help.url', { defaultValue: undefined });

  const [isWriteAccess, setWriteAccess] = React.useState(false);

  React.useEffect(() => {
    const offList = events.on("list-secrets", (data) => handleList(data));
    return () => {
      offList();
    };
  }, []);

  const handleList = (e) => {
    setWriteAccess(e?.write);
    setInstance(e.data);
    setDrawer(true);
  };

  React.useEffect(() => {
    if (instance) {
      loadSecrets();
    }
  }, [instance]);

  const loadSecrets = async () => {
    try {
      const { data: secretsData, error: secretsError } = await database.rpc(
        "get_secrets_by_instance",
        { qinstance_id: instance.id }
      );
      if (secretsError) throw secretsError;
      setSecrets(
        secretsData.map((s) => ({
          ...s,
          name: s.name.replace(/^[0-9a-fA-F-]{36}_/, ""),
        }))
      );
    } catch (error) {
      events.dispatch("alert-message", {
        data: {
          message: t('secrets.loading.error'),
          severity: "error",
        },
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({});
    setFormErrors(null);
    setHasTriedToSave(false);
  };

  const editSecret = (secret) => {
    setOpen(true);
    setFormData(secret);
  };

  const newSecret = () => {
    setOpen(true);
    setFormData({
      id: null,
      quniqname: "",
      secret: "",
      description: "",
    });
  };

  const deleteSecret = async (secret) => {
    const confirmed = await dialogs.confirm(
      t('secrets.delete.confirm', { name: secret.name }),
      { okText: t('commons.yes'), cancelText: t('commons.no') }
    );
    if (confirmed) {
      try {
        const { error } = await database.rpc("delete_secret", {
          qsecret_id: secret.id,
        });
        if (error) throw error;
        loadSecrets(); // Refresh the list after deletion
      } catch (error) {
        events.dispatch("alert-message", {
          data: {
            message: t('secrets.delete.error', { name: secret.name }),
            severity: "error",
          },
        });
      }
    }
  };

  const save = async () => {
    setHasTriedToSave(true);

    if (formErrors && formErrors.length) {
      events.dispatch("alert-message", {
        data: { message: t('commons.errors.fix_errors'), severity: "error" },
      });
      return;
    }

    try {
      if (formData.id == null) {
        const { error } = await database.rpc("create_secret", {
          quniqname: `${instance.id}_${formData.name}`,
          qsecret: formData.secret,
          qdescription: formData?.description?.length
            ? formData.description
            : "",
        });
        if (error) throw error;
      } else {
        const d = {
          qsecret_id: formData.id,
          quniqname: `${instance.id}_${formData.name}`,
          qsecret: formData.secret?.length ? formData.secret : null,
          qdescription: formData?.description?.length
            ? formData.description
            : "",
        };
        const { error } = await database.rpc("update_secret", d);
        if (error) throw error;
      }
      handleClose();
      loadSecrets(); // Refresh the list after saving
    } catch (error) {
      events.dispatch("alert-message", {
        data: {
          message: t('secrets.save.error'),
          severity: "error",
        },
      });
    }
  };

  const setFormResult = ({ data, errors }) => {
    setFormData(data);
    setFormErrors(errors);
  };

  return (
    <React.Fragment>
      <Drawer
        open={isDrawer}
        anchor="right"
        sx={{ overflow: "none" }}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setDrawer(false);
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Typography>
            {instance?.name}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1}>
            {isWriteAccess && <Button
              onClick={newSecret}
              startIcon={<AddIcon />}
              variant="outlined"
              color="success"
            >
              {t('secrets.save.new')}
            </Button>}
            <Button onClick={() => setDrawer(false)}>{t('buttons.close')}</Button>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            width: {
              xs: "90vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            }
          }}
        >
          {helpSecretsMsg &&
            <Alert severity="info" onClose={() => { }}
              action=
              {helpSecretsLink && <Button color="inherit" size="small" onClick={() => {
                window.open(helpSecretsLink, "_blank");
              }}>
                {t('buttons.help')}
              </Button>}
            >
              {helpSecretsMsg}
            </Alert>
          }
          <List sx={{ width: "100%" }}>
            {secrets.map((secret) => {
              return (
                <ListItem
                  key={secret.id}
                  secondaryAction={isWriteAccess && 
                    <>
                      <IconButton onClick={() => editSecret(secret)}>
                        <SettingsSuggestIcon />
                      </IconButton>
                      <IconButton onClick={() => deleteSecret(secret)}>
                        <DeleteIcon />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemText primary={secret.name} secondary={secret.description} />
                </ListItem>
              );
            })}
          </List>

        </DialogContent>
      </Drawer>

      <Drawer
        open={open}
        anchor="right"
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Box>Secret for {instance?.name}</Box>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button onClick={save} autoFocus
              startIcon={<AddIcon />}
              variant="outlined"
              color="success">
              {t('buttons.save')}
            </Button>
            <Button onClick={handleClose}>{t('buttons.cancel')}</Button>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{
          width: {
            xs: "90vw",
            sm: "80vw",
            md: "70vw",
            lg: "60vw",
            xl: "50vw",
          } }}>
          <Box sx={{ width: "100%", paddingTop: "20px" }} className="jsonforms">
            <JsonForms
              schema={schema}
              uischema={uischema}
              data={formData}
              renderers={materialRenderers}
              cells={materialCells}
              onChange={setFormResult}
              validationMode={
                hasTriedToSave ? "ValidateAndShow" : "ValidateAndHide"
              }
            />
          </Box>
        </DialogContent>
      </Drawer>
    </React.Fragment>
  );
}
